import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { statusTags } from '../../constants/statusTags';
import { useOpportunityTableColumns } from '../hooks/use-opportunity-table-columns';
import { filterSelectedStatusTag } from '../helpers/statusTags';
import useMunicipality from '../hooks/use-municipality';
import useStatusTags, { StatusFilterType } from '../hooks/use-status-tags';
import useGeonorge from '../../hooks/use-geonorge';
import ResetFilterButton from 'src/components/ResetFilterButton/ResetFilterButton';
import DataTableV3, {
  DataTableV3PaginationListenersProps,
  DataTableV3Actions,
} from '../../../../components/DataTableV3';
import { DataTableV3Ids } from 'src/constants/dataTableIds';
import { useSearchParams } from 'react-router-dom';
import FilterDateInput from 'src/components/Filters/FilterDateInput';
import QueryParamMultiSelect from 'src/components/Filters/QueryParamMultiSelect';
import { OneWayInOpportunitySimpleV2Dto } from 'src/api/v2';
import useDeliveredServices from '../hooks/use-delivered-services';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';

type OneWayInOpportunityTableTypes = {
  opportunities: OneWayInOpportunitySimpleV2Dto[];
  paginationListeners: DataTableV3PaginationListenersProps;
  pageSize: number;
  overridePaginationTotalNumber: number;
  selectedRows: OneWayInOpportunitySimpleV2Dto[];
  setSelectedRows: React.Dispatch<React.SetStateAction<OneWayInOpportunitySimpleV2Dto[]>>;
  setShowArchiveDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setShowDeliverServicesDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  setShowRecommendServicesDialog?: React.Dispatch<React.SetStateAction<boolean>>;
};

const queryParams = {
  fromDate: 'fromDate',
  toDate: 'toDate',
  lastChangedDate: 'lastChangedOn',
  municipality: 'municipality',
  status: 'status',
  deliveredServices: 'deliveredServices',
};

const OneWayInOpportunityTable: React.FC<OneWayInOpportunityTableTypes> = ({
  opportunities,
  isLoading,
  paginationListeners,
  pageSize,
  overridePaginationTotalNumber,
  selectedRows,
  setSelectedRows,
  setShowDeliverServicesDialog,
  setShowArchiveDialog,
  setShowRecommendServicesDialog,
}) => {
  const { t: tCommon } = useTranslation();
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tTableTitles } = useTranslation('tableTitles');

  const [searchParams, setSearchParams] = useSearchParams();
  const columns = useOpportunityTableColumns();
  const { getMunicipalityNameByNumber } = useGeonorge();
  const siteimprove = useSiteimprove();

  const onDeliverServicesModalOpen = () => {
    setShowDeliverServicesDialog?.(true);
    siteimprove.log(
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_TABLE.id,
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_TABLE.actions.OPEN_DELIVER_SERVICES_MODAL,
    );
  };

  const onRecommendServicesModalOpen = () => {
    setShowRecommendServicesDialog?.(true);
    siteimprove.log(
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_TABLE.id,
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_TABLE.actions.OPEN_RECOMMEND_SERVICES_MODAL,
    );
  };

  const actions: DataTableV3Actions<OneWayInOpportunitySimpleV2Dto> = {
    secondary: [
      {
        icon: { prefix: 'fal', iconName: 'hand-holding-box' },
        label: tOneWayIn('button.RecommendService'),
        onClick: onRecommendServicesModalOpen,
        disabled: selectedRows.length === 0,
      },
      {
        icon: { prefix: 'fal', iconName: 'grid-2-plus' },
        label: tOneWayIn('customerFollowUp.deliverService'),
        onClick: onDeliverServicesModalOpen,
        disabled: selectedRows.length === 0,
      },
      {
        icon: { prefix: 'fal', iconName: 'folder-open' },
        label: tOneWayIn('batchArchive.archive'),
        onClick: () => setShowArchiveDialog?.(true),
        disabled: selectedRows.length === 0,
      },
    ],
  };

  const { selectedMunicipalityIds, selectedStatusIds, fromDate, toDate, selectedDeliveredServiceIds } =
    useMemo(() => {
      return {
        selectedMunicipalityIds: searchParams.get(queryParams.municipality)?.split(',') ?? [],
        selectedStatusIds: searchParams.get(queryParams.status)?.split(',') ?? [],
        fromDate: searchParams.get(queryParams.fromDate) ?? '',
        toDate: searchParams.get(queryParams.toDate) ?? '',
        selectedDeliveredServiceIds: searchParams.get(queryParams.deliveredServices)?.split(',') ?? [],
      };
    }, [searchParams]);

  const filtersActive = useMemo(() => {
    if (
      selectedMunicipalityIds.length > 0 ||
      selectedStatusIds.length > 0 ||
      fromDate.length > 0 ||
      toDate.length > 0
    ) {
      return true;
    }
    return false;
  }, [selectedMunicipalityIds.length, selectedStatusIds.length, fromDate.length, toDate.length]);

  const { statusOptions } = useStatusTags();
  const { municipalityOptions } = useMunicipality(opportunities);
  const { deliveredServicesOptions } = useDeliveredServices(opportunities);

  const convertedMunicipalityOptions = useMemo(() => {
    return municipalityOptions
      .map((municipality) => ({
        label:
          getMunicipalityNameByNumber(municipality.text) ||
          tOneWayIn('opportunity.table.filter.noMunicipality'),
        value: municipality.value || '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'no-nb'));
  }, [municipalityOptions, getMunicipalityNameByNumber, tOneWayIn]);

  const filterByMunicipality = (item: OneWayInOpportunitySimpleV2Dto): boolean => {
    if (selectedMunicipalityIds.length === 0) return true;
    //Håndtering for når en henvendelse ikke har satt kommune
    if (!item.municipality) return selectedMunicipalityIds.includes('');
    return selectedMunicipalityIds.includes(item.municipality.toLowerCase());
  };

  const filterByDeliveredServices = (item: OneWayInOpportunitySimpleV2Dto): boolean => {
    if (selectedDeliveredServiceIds.length === 0) return true;
    return selectedDeliveredServiceIds.some((serviceId) =>
      item.deliveredServices?.some((service) => service.id === serviceId),
    );
  };

  const filterByStatusTags = (item: OneWayInOpportunitySimpleV2Dto): boolean => {
    const selectedStatuses = selectedStatusIds.map((id) => id as StatusFilterType);

    if (selectedStatuses.length === 0) return true;

    const filterStatusTagValue = selectedStatuses.flatMap((selectedStatus) => statusTags[selectedStatus]);

    return filterSelectedStatusTag(item, filterStatusTagValue);
  };

  const filterByDateRange = (item: OneWayInOpportunitySimpleV2Dto): boolean => {
    if (item?.submittedOn === undefined) return false;
    const submittedDate = new Date(item?.submittedOn);
    return (
      (fromDate ? submittedDate >= new Date(fromDate) : true) &&
      (toDate ? submittedDate <= new Date(toDate) : true)
    );
  };

  const tableData: OneWayInOpportunitySimpleV2Dto[] = opportunities
    .filter(filterByMunicipality)
    .filter(filterByStatusTags)
    .filter(filterByDateRange)
    .filter(filterByDeliveredServices);

  function handleClearFilter() {
    if (searchParams.get('activeTab') === 'archive') setSearchParams('activeTab=archive', { replace: true });
    else setSearchParams(undefined, { replace: true });
  }

  const filteredStatusOptions = useMemo(() => {
    const openStatusOptions = statusOptions.filter((so) => so.value !== 'archived');
    return { openStatusOptions };
  }, [statusOptions]);

  const filters = (
    <div className="display--flex flex-wrap--wrap align-items--flex-end margin-bottom--6 gap--2 margin-0-on-all-children">
      <QueryParamMultiSelect
        queryStringParam={queryParams.municipality}
        options={convertedMunicipalityOptions}
        name="municipality"
        label={tCommon('municipality')}
        placeHolder={tCommon('chooseMunicipality')}
      />
      {searchParams.get('activeTab') !== 'archive' && (
        <QueryParamMultiSelect
          queryStringParam={queryParams.status}
          options={filteredStatusOptions.openStatusOptions}
          name={'status'}
          label={tCommon('status')}
        />
      )}
      <QueryParamMultiSelect
        queryStringParam={queryParams.deliveredServices}
        options={deliveredServicesOptions}
        name={'deliveredServices'}
        label={tOneWayIn('services.delivered')}
      />
      <div className="flex-grow--1">
        <FilterDateInput queryStringParam={queryParams.fromDate} label={tCommon('fromDate')} />
      </div>
      <div className="flex-grow--1">
        <FilterDateInput queryStringParam={queryParams.toDate} label={tCommon('toDate')} />
      </div>
      <ResetFilterButton className="margin-top--2" onClick={handleClearFilter} />
    </div>
  );

  return (
    <>
      <DataTableV3
        id={DataTableV3Ids.OneWayIn.Opportunities}
        actions={searchParams.get('activeTab') === 'archive' ? undefined : actions}
        disableSelection={searchParams.get('activeTab') === 'archive' ? true : false}
        loading={isLoading}
        customColumnFilters={filters}
        data={tableData}
        columns={columns}
        translationFn={(key: string) => tTableTitles(key as any)}
        texts={{
          globalFilterPlaceholder: tOneWayIn('opportunitySearchInputPlaceHolder'),
          rowsPerPage: tCommon('rowsPerPage'),
          of: tCommon('of'),
        }}
        sorting={[
          {
            desc: true,
            id: 'submittedDate',
          },
        ]}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        pageSize={pageSize}
        filtersActive={filtersActive}
        paginationListeners={paginationListeners}
        overridePaginationTotalNumber={overridePaginationTotalNumber}
        autoResetPageIndex={false}
        showRowsSelectedText={searchParams.get('activeTab') === 'archive' ? false : true}
        setSelectedRows={setSelectedRows}
        hideFilterText
      />
    </>
  );
};

export default OneWayInOpportunityTable;
